import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { FaPlus, FaSave, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import { catalogueContentApi } from "../../api/services";
import { getErrorMessage } from "../../api/client";
import CmsImageField from "../../components/CmsImageField";
import CmsFileField from "../../components/CmsFileField";
import { FormPageSkeleton } from "../../components/Skeleton";
import "./AdminHomeContent.css";

const isCatalogueUploadUrl = (url) =>
  typeof url === "string" && url.startsWith("/uploads/catalogue/");

const isBlobUrl = (url) => typeof url === "string" && url.startsWith("blob:");

const cloneContent = (value) => JSON.parse(JSON.stringify(value));

const collectCatalogueUrls = (content) => {
  const urls = [];
  (content?.items || []).forEach((item) => {
    if (isCatalogueUploadUrl(item?.image)) urls.push(item.image);
    if (isCatalogueUploadUrl(item?.fileUrl)) urls.push(item.fileUrl);
  });
  return urls;
};

const emptyItem = () => ({
  title: "",
  image: "",
  fileUrl: "",
  buttonText: "View & Download Here",
  sortOrder: 0,
  isActive: true,
});

const AdminCatalogueContent = () => {
  const [content, setContent] = useState(null);
  const [savedContent, setSavedContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const pendingFilesRef = useRef(new Map());

  const setPendingFile = useCallback((fieldKey, file) => {
    if (file) pendingFilesRef.current.set(fieldKey, file);
    else pendingFilesRef.current.delete(fieldKey);
  }, []);

  const reindexPending = useCallback((removedIndex) => {
    const map = pendingFilesRef.current;
    const fields = ["image", "fileUrl"];
    fields.forEach((field) => {
      map.delete(`items.${removedIndex}.${field}`);
    });

    const higher = [...map.entries()]
      .map(([key, file]) => {
        const match = key.match(/^items\.(\d+)\.(image|fileUrl)$/);
        if (!match) return null;
        const index = Number(match[1]);
        if (Number.isNaN(index) || index <= removedIndex) return null;
        return { index, field: match[2], file, key };
      })
      .filter(Boolean)
      .sort((a, b) => a.index - b.index);

    higher.forEach(({ key }) => map.delete(key));
    higher.forEach(({ index, field, file }) => {
      map.set(`items.${index - 1}.${field}`, file);
    });
  }, []);

  const uploadPending = useCallback(async (items) => {
    const next = items.map((item) => ({ ...item }));
    const pending = pendingFilesRef.current;

    for (let i = 0; i < next.length; i += 1) {
      const imageKey = `items.${i}.image`;
      const fileKey = `items.${i}.fileUrl`;

      if (pending.has(imageKey)) {
        const res = await catalogueContentApi.uploadImages([pending.get(imageKey)]);
        const url = res.data.data.urls?.[0] || "";
        if (isBlobUrl(next[i].image)) URL.revokeObjectURL(next[i].image);
        next[i].image = url;
        pending.delete(imageKey);
      } else if (isBlobUrl(next[i].image)) {
        URL.revokeObjectURL(next[i].image);
        next[i].image = "";
      }

      if (pending.has(fileKey)) {
        const res = await catalogueContentApi.uploadFiles([pending.get(fileKey)]);
        const url = res.data.data.urls?.[0] || "";
        if (isBlobUrl(next[i].fileUrl)) URL.revokeObjectURL(next[i].fileUrl);
        next[i].fileUrl = url;
        pending.delete(fileKey);
      } else if (isBlobUrl(next[i].fileUrl)) {
        URL.revokeObjectURL(next[i].fileUrl);
        next[i].fileUrl = "";
      }
    }

    return next;
  }, []);

  const deleteReplacedFiles = useCallback(async (previous, next) => {
    const prev = new Set(collectCatalogueUrls(previous));
    const kept = new Set(collectCatalogueUrls(next));
    const removals = [...prev].filter((url) => !kept.has(url));
    await Promise.all(
      removals.map(async (url) => {
        try {
          await catalogueContentApi.deleteFile(url);
        } catch {
          // ignore
        }
      })
    );
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await catalogueContentApi.get();
      const loaded = res.data.data.content;
      setContent(loaded);
      setSavedContent(cloneContent(loaded));
      pendingFilesRef.current.clear();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load catalogue content"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patchHero = (field, value) => {
    setContent((prev) => ({
      ...prev,
      hero: { ...prev.hero, [field]: value },
    }));
  };

  const updateItem = (index, field, value) => {
    setContent((prev) => {
      const items = [...(prev.items || [])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setContent((prev) => {
      const items = [...(prev.items || []), emptyItem()];
      items.forEach((item, i) => {
        item.sortOrder = i;
      });
      return { ...prev, items };
    });
  };

  const removeItem = (index) => {
    setContent((prev) => {
      const removed = prev.items?.[index];
      if (isBlobUrl(removed?.image)) URL.revokeObjectURL(removed.image);
      if (isBlobUrl(removed?.fileUrl)) URL.revokeObjectURL(removed.fileUrl);
      reindexPending(index);
      const items = (prev.items || []).filter((_, i) => i !== index);
      items.forEach((item, i) => {
        item.sortOrder = i;
      });
      return { ...prev, items };
    });
    toast.info("Item removed — click Save to apply");
  };

  const saveAll = async () => {
    if (!content) return;
    setSaving(true);
    try {
      const items = await uploadPending(content.items || []);
      const payload = {
        hero: content.hero,
        items,
      };
      const res = await catalogueContentApi.update(payload);
      const nextContent = res.data.data.content;
      await deleteReplacedFiles(savedContent, nextContent);
      pendingFilesRef.current.clear();
      setContent(nextContent);
      setSavedContent(cloneContent(nextContent));
      toast.success("Catalogue page saved");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !content) {
    return (
      <div className="manage-page">
        <FormPageSkeleton />
      </div>
    );
  }

  return (
    <div className="manage-page">
      <header className="manage-head">
        <div>
          <h1>Catalogue page</h1>
          <p>Manage catalogue titles, cover images, and downloadable PDF files.</p>
        </div>
        <Button variant="dark" disabled={saving} onClick={saveAll}>
          {saving ? <Spinner size="sm" animation="border" /> : <FaSave />} Save all
        </Button>
      </header>

      <div className="home-cms-card mb-4">
        <h3 className="mb-3">Page header</h3>
        <Form.Group className="mb-3">
          <Form.Label>Title</Form.Label>
          <Form.Control
            value={content.hero?.title || ""}
            onChange={(e) => patchHero("title", e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Subtitle</Form.Label>
          <Form.Control
            value={content.hero?.subtitle || ""}
            onChange={(e) => patchHero("subtitle", e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-0">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={content.hero?.description || ""}
            onChange={(e) => patchHero("description", e.target.value)}
          />
        </Form.Group>
      </div>

      <div className="home-cms-card">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">Catalogue items</h3>
          <Button variant="outline-dark" onClick={addItem}>
            <FaPlus /> Add catalogue
          </Button>
        </div>

        {(content.items || []).map((item, index) => (
          <div className="home-cms-item" key={item._id || index}>
            <div className="home-cms-item-head">
              <h4>
                {item.title || `Catalogue ${index + 1}`}
              </h4>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => removeItem(index)}
              >
                <FaTrash />
              </Button>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={item.title || ""}
                onChange={(e) => updateItem(index, "title", e.target.value)}
              />
            </Form.Group>

            <CmsImageField
              label="Cover image (optional)"
              value={item.image}
              onChange={(v) => updateItem(index, "image", v)}
              onPendingFile={(file) => setPendingFile(`items.${index}.image`, file)}
            />

            <CmsFileField
              label="PDF / document"
              value={item.fileUrl}
              onChange={(v) => updateItem(index, "fileUrl", v)}
              onPendingFile={(file) => setPendingFile(`items.${index}.fileUrl`, file)}
            />

            <Form.Group className="mb-3">
              <Form.Label>Button text</Form.Label>
              <Form.Control
                value={item.buttonText || ""}
                onChange={(e) => updateItem(index, "buttonText", e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Or paste external download URL</Form.Label>
              <Form.Control
                value={
                  item.fileUrl && !isBlobUrl(item.fileUrl) ? item.fileUrl : ""
                }
                onChange={(e) => {
                  setPendingFile(`items.${index}.fileUrl`, null);
                  updateItem(index, "fileUrl", e.target.value);
                }}
                placeholder="https://... or /uploads/catalogue/..."
              />
            </Form.Group>

            <Form.Check
              type="switch"
              id={`catalogue-active-${index}`}
              label="Active"
              checked={item.isActive !== false}
              onChange={(e) => updateItem(index, "isActive", e.target.checked)}
            />
          </div>
        ))}

        <div className="home-cms-actions">
          <Button variant="dark" disabled={saving} onClick={saveAll}>
            {saving ? <Spinner size="sm" animation="border" /> : <FaSave />} Save all
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminCatalogueContent;
